import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Button } from "@/components/ui/button";
import { UseConversationDetailsReturn } from "@/hooks/conversation/useConversationDetails";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationActionsTabProps {
  state: UseConversationDetailsReturn;
}

/**
 * "Actions" tab of the conversation analysis: re-run the analysis and email
 * the report, each with a plain description of what it does.
 *
 * Owner-facing HG brand chrome; the buttons keep the genie's own colour, as
 * they did in the page header and footer they moved from.
 */
export function ConversationActionsTab({ state }: ConversationActionsTabProps) {
  const {
    currentConversation,
    loading,
    emailLoading,
    analysisResult,
    supportTeamEmails,
    isAnalyzeDisabled,
    getAnalyzeDisabledReason,
    handleRunAI,
    handleSendEmail,
  } = state;
  const { user } = useAuth();

  const primaryColor = currentConversation?.agent?.branding?.primaryColor || "#4E9CFF";
  const buttonStyle = { backgroundColor: primaryColor, borderColor: primaryColor };
  const hasRecipients = supportTeamEmails.length > 0;
  const analyzeDisabled = loading || isAnalyzeDisabled;
  const sendDisabled = emailLoading || loading || !analysisResult;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ActionCard
        genieIcon="brain"
        title="Analyze this conversation"
        description="Reads the transcript again and rebuilds the summary, insights, topics and captured contact details. Use it after you change what your genie captures, or when the analysis looks incomplete. The current analysis is replaced."
      >
        <Button
          onClick={handleRunAI}
          disabled={analyzeDisabled}
          title={getAnalyzeDisabledReason || undefined}
          className="min-h-[44px] w-full sm:w-auto px-6 text-white flex items-center gap-2 hover:opacity-80 disabled:opacity-50"
          style={buttonStyle}
        >
          {loading && <Loader2 className="animate-spin h-4 w-4" />}
          {loading ? "Analyzing..." : "Analyze"}
        </Button>
      </ActionCard>

      <ActionCard
        genieIcon="mail"
        title="Email the report"
        description="Sends this conversation's report by email: the summary, the insights and the full transcript."
      >
        <div className="rounded-xl border border-gray-200 bg-[#F9FAFC] p-4">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2">Who receives it</div>
          {hasRecipients ? (
            <ul className="space-y-1">
              {supportTeamEmails.map((email: string) => (
                <li key={email} className="text-sm text-gray-700 break-all">
                  <CopyableEmail email={email} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed">
              No one is set up to receive this genie's reports yet, so we'll send this one to you
              {user?.email ? (
                <>
                  {" "}
                  at <span className="font-semibold break-all">{user.email}</span>
                </>
              ) : null}
              .
            </p>
          )}
          <Link
            to={`/genie-hub/my-genies/${currentConversation.agent_id}?section=section-reports`}
            className="mt-2 inline-flex min-h-[44px] items-center text-sm"
          >
            <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
              {hasRecipients ? "Change who receives reports →" : "Choose who receives reports →"}
            </span>
          </Link>
        </div>

        <Button
          onClick={handleSendEmail}
          disabled={sendDisabled}
          className="min-h-[44px] w-full sm:w-auto px-6 text-white flex items-center gap-2 hover:opacity-80 disabled:opacity-50"
          style={buttonStyle}
        >
          {emailLoading && <Loader2 className="animate-spin h-4 w-4" />}
          {emailLoading ? "Sending..." : "Get Report On Email"}
        </Button>
        {!analysisResult && !loading && (
          <p className="text-xs text-gray-500">
            The report is built from the analysis. Analyze this conversation first.
          </p>
        )}
      </ActionCard>
    </div>
  );
}

interface ActionCardProps {
  genieIcon: GenieIconName;
  title: string;
  description: string;
  children: React.ReactNode;
}

function ActionCard({ genieIcon, title, description, children }: ActionCardProps) {
  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="h-full rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon name={genieIcon} size={24} color="#4E9CFF" secondaryColor="#FF4DB8" gradientEnabled label={title} />
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
          </div>
        </div>
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
